import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Input } from '@repo/apg-ui';
import { ReactComponent as InfoIcon } from '../../assets/icons/new/info-dark.svg';
import * as _actionsSearch from '../../actions/search';
import { RootState } from '../../reducers';
import { bindActionCreators, Dispatch } from 'redux';
import * as actions from '../../actions';
import { withRouter } from '../../utils/withRouter';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';
import SpinnerV2 from '../SpinnerV2';
import { ReactComponent as ShevronRightIcon } from '../../assets/icons/new/shevron-right.svg';
import classNames from 'classnames';
import './FastbookInput.scss';
import Tooltip from 'rc-tooltip';
import { PartiallyIntegratedUserDataEC } from '../../reducers/app';

interface Props {
  actionsSearch: typeof _actionsSearch;
  userDataEC: PartiallyIntegratedUserDataEC;
  intl: any;
}

const FastBookSearchInput = ({ actionsSearch, userDataEC, intl }: Props) => {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [abortController, setAbortController] = useState(undefined);
  const ref = useRef(null);

  const createNewAbortController = () => {
    const controller = new AbortController();
    setAbortController(controller);
    return controller;
  };

  const handleClickOutside = (event) => {
    if (
      ref.current &&
      !ref.current?.contains(event.target) &&
      event.target?.className &&
      !String(event.target?.className).includes('fastbook')
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const renderAvailableProductIds = () => {
    if (userDataEC.fastbook.isExist) {
      return (
        <div>
          {userDataEC.fastbook.urnlist.map((item, idx) => (
            <Tooltip overlayClassName={'rc-tooltip-bg-white'} placement={'bottom'} overlay={item.label}>
              <div
                className='d-flex align-items-center justify-content-between fastbook--item cursor-pointer'
                key={`fastbook-urn${idx}`}
                onClick={() => {
                  navigate({ search: `?modalContent=fastbook&urn=${item.urn}` });
                  setValue('');
                  actionsSearch.setFastbookData({
                    ids: [],
                    urnlist: [],
                    isExist: false,
                    isLoading: false,
                  });
                  setIsOpen(false);
                }}
              >
                <span>{item.label.slice(0, 15)}</span>
                <ShevronRightIcon />
              </div>
            </Tooltip>
          ))}
        </div>
      );
    } else {
      return (
        <div className={'fastbook--item'}>
          <FormattedMessage id={'ec.search.fastbook.notfound'} defaultMessage={'Produkt-ID nicht gefunden'} />
        </div>
      );
    }
  };

  const fetchSearchResults = (_value) => {
    if (abortController) {
      abortController.abort();
    }

    _value = _value.trim();

    const controller = createNewAbortController();

    actionsSearch
      .getSearchResults({
        context: 'id',
        searchterm: _value,
        signal: controller.signal,
      })
      .then((res) => {
        if (res?.data?.urnlist) {
          actionsSearch.setFastbookData({
            ids: [_value],
            urnlist: res.data.urnlist,
            isExist: res.data.urnlist.length > 0,
            isLoading: false,
          });
        } else {
          actionsSearch.setFastbookData({
            ids: [],
            urnlist: [],
            isExist: false,
            isLoading: false,
          });
        }
        setAbortController(undefined);
      });
  };

  const onValueChange = (_value) => {
    if (_value) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    if (_value && !userDataEC.fastbook.isLoading) {
      actionsSearch.setFastbookData({
        isLoading: true,
      });
    }
    setValue(_value);
    setTimeout(() => {
      setValue((prevValue) => {
        // This is a trick to get current value of state
        if (prevValue === _value) {
          fetchSearchResults(prevValue);
        }
        return prevValue;
      });
    }, 700);
  };

  return (
    <div className='d-flex align-items-center border-start me-4 fastbook' ref={ref}>
      <div className='text-16 d-flex align-items-center text-dark font-bold ps-24'>
        <FormattedMessage id='ec.search.fastbook.label' defaultMessage='Fast booking' />
        <div className='ms-3 cursor-pointer me-4'>
          <Tooltip
            overlayClassName={'rc-tooltip-bg-white'}
            placement={'top'}
            overlay={
              <b>
                <FormattedMessage
                  id='ec.fastbook.placeholder.tooltip'
                  defaultMessage='Abfrage von Einzelflächen bei Eingabe einer Produkt-ID'
                />
              </b>
            }
          >
            <InfoIcon />
          </Tooltip>
        </div>
      </div>
      <Input
        value={value}
        name={'fastbookId'}
        onFocus={() => {
          if (userDataEC.fastbook.isExist) {
            setIsOpen(true);
          }
        }}
        onChange={(name, _value) => {
          onValueChange(_value);
        }}
        type='number'
        placeholder={intl.formatMessage({
          id: 'ec.search.fastbook.placeholder',
          defaultMessage: 'Produkt-ID',
        })}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && userDataEC.fastbook.isExist) {
            navigate({ search: `?modalContent=fastbook&urn=${userDataEC.fastbook.ids[0]}` });
          }
        }}
        inputWrapperClass='mt-0 fastbook-input'
      ></Input>
      <div className={classNames('fastbook__dropdown', { open: isOpen })}>
        {userDataEC.fastbook.isLoading ? (
          <span>
            <SpinnerV2 relative={true} style={{ margin: '12px' }} />
          </span>
        ) : (
          renderAvailableProductIds()
        )}
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
  };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
  return {
    actionsSearch: bindActionCreators(_actionsSearch as any, dispatch),
    dispatch: dispatch,
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(FastBookSearchInput)));
